import localforage from 'localforage';

import { ECexName } from 'web3';

const STORE_NAME = 'order-book-snapshots';
const STORE_CONFIG = {
  name: STORE_NAME,
  storeName: 'snapshots',
};

const storage = localforage.createInstance(STORE_CONFIG);

export interface CacheStats {
  exchange: ECexName;
  symbol: string;
  chunkCount: number;
  estimatedSizeBytes: number;
  oldestTimestamp: number | null;
  newestTimestamp: number | null;
}

/**
 * Calculate estimated size of stored data
 * This is an approximation based on JSON serialization
 */
const estimateSize = (data: unknown): number => {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
};

/**
 * Build pattern for matching all chunks of a specific exchange and symbol
 */
const buildKeyPattern = (exchange: ECexName, symbol: string): string => `ob:${exchange}:${symbol}:`;

/**
 * Get cache statistics for a specific exchange and symbol
 */
export const getCacheStats = async (
  exchange: ECexName,
  symbol: string,
): Promise<CacheStats | null> => {
  try {
    await storage.ready();

    const keyPattern = buildKeyPattern(exchange, symbol);
    const allKeys = await storage.keys();
    const matchingKeys = allKeys.filter(key => key.startsWith(keyPattern));

    if (matchingKeys.length === 0) {
      return null;
    }

    let totalSize = 0;
    let oldestTimestamp: number | null = null;
    let newestTimestamp: number | null = null;

    // Load all chunks and calculate statistics
    const chunkPromises = matchingKeys.map(async key => {
      const chunk = await storage.getItem(key);
      if (chunk) {
        const size = estimateSize(chunk);
        totalSize += size;

        // Extract timestamps from chunk data
        const chunkData = chunk as { chunkStart: number; snapshots: Array<{ timestamp: number }> };
        if (chunkData.snapshots && chunkData.snapshots.length > 0) {
          const firstTs = chunkData.snapshots[0].timestamp;
          const lastTs = chunkData.snapshots[chunkData.snapshots.length - 1].timestamp;

          if (oldestTimestamp === null || firstTs < oldestTimestamp) {
            oldestTimestamp = firstTs;
          }
          if (newestTimestamp === null || lastTs > newestTimestamp) {
            newestTimestamp = lastTs;
          }
        }
      }
    });

    await Promise.all(chunkPromises);

    return {
      exchange,
      symbol,
      chunkCount: matchingKeys.length,
      estimatedSizeBytes: totalSize,
      oldestTimestamp,
      newestTimestamp,
    };
  } catch (error) {
    console.error('[CacheStats] Failed to get cache stats', { exchange, symbol, error });
    return null;
  }
};

/**
 * Get all cache statistics for all exchanges and symbols
 */
export const getAllCacheStats = async (): Promise<CacheStats[]> => {
  try {
    await storage.ready();

    const allKeys = await storage.keys();

    // Extract unique exchange:symbol combinations
    const combinations = new Set<string>();

    for (const key of allKeys) {
      // Key format: "ob:{exchange}:{symbol}:{chunkStart}"
      if (key.startsWith('ob:')) {
        const parts = key.split(':');
        if (parts.length >= 3) {
          const exchange = parts[1];
          const symbol = parts[2];
          combinations.add(`${exchange}:${symbol}`);
        }
      }
    }

    // Get stats for each combination
    const statsPromises = Array.from(combinations).map(combo => {
      const [exchange, symbol] = combo.split(':');
      return getCacheStats(exchange as ECexName, symbol);
    });

    const results = await Promise.all(statsPromises);

    return results.filter((stats): stats is CacheStats => stats !== null);
  } catch (error) {
    console.error('[CacheStats] Failed to get all cache stats', error);
    return [];
  }
};

/**
 * Clear cache for specific exchange and symbol
 */
export const clearCache = async (exchange: ECexName, symbol: string): Promise<void> => {
  try {
    await storage.ready();

    const keyPattern = buildKeyPattern(exchange, symbol);
    const allKeys = await storage.keys();
    const matchingKeys = allKeys.filter(key => key.startsWith(keyPattern));

    await Promise.all(matchingKeys.map(key => storage.removeItem(key)));

    console.log(`[CacheStats] Cleared ${matchingKeys.length} chunks for ${exchange}:${symbol}`);
  } catch (error) {
    console.error('[CacheStats] Failed to clear cache', { exchange, symbol, error });
    throw error;
  }
};

/**
 * Clear all cache
 */
export const clearAllCache = async (): Promise<void> => {
  try {
    await storage.ready();
    await storage.clear();
    console.log('[CacheStats] Cleared all cache');
  } catch (error) {
    console.error('[CacheStats] Failed to clear all cache', error);
    throw error;
  }
};

/**
 * Format bytes to human-readable size
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};
